import React, { useEffect, useState } from "react";
import {
  FiX,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiEdit3,
  FiLoader,
  FiFileText,
  FiTrash2,
  FiClock,
  FiActivity,
  FiCheck,
  FiXCircle,
  FiChevronDown,
} from "react-icons/fi";

import { useSelector, useDispatch } from "react-redux";
import {
  selectOrders,
  selectOrderById,
  clearOrderById,
  clearOrdersError,
} from "../../../store/slices/orders/slice";
import {
  getOrders,
  getOrderById,
  updateOrderStatus,
  deleteOrder,
} from "../../../store/slices/orders/asyncThunks";

import {
  StatusBadge,
  PageHeader,
  Button,
  ActionButtonGroup,
  Modal,
  ModalClose,
  ModalContent,
  ModalHeader,
  ModalTitle,
  PageSection,
  PageTitle,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  TableRow,
  PaginationContainer,
  PaginationButton,
  PaginationInfo,
  CustomerInfoContainer,
  CustomerInfoTitle,
  CustomerInfoGrid,
  CustomerInfoItem,
  CustomerInfoLabel,
  CustomerInfoValue,
  OrderItemsContainer,
  OrderItemsTitle,
  OrderSummaryContainer,
  OrderSummaryGrid,
  OrderSummaryItem,
  StatusChangeModal,
  StatusChangeContent,
  StatusChangeTitle,
  StatusOptionsContainer,
  StatusOption,
  ConfirmModal,
  FilterSelect,
  Relative,
} from "./AdminOrdersPage.styles";

import { formatDate, formatPrice } from "../../../utils/utils";
import AdminContainer from "../../../components/utils/AdminContainer";
import { AnimatePresence, motion } from "framer-motion";
import Loader from "../../../components/utils/Loader";
import { toast } from "../../../components/modern-ui/sonner";

const statusOptions = [
  { value: "pending", label: "Na čekanju" },
  { value: "processing", label: "U obradi" },
  { value: "shipped", label: "Poslano" },
  { value: "delivered", label: "Dostavljeno" },
  { value: "canceled", label: "Otkazano" },
];

const getOrderStatusIcon = (status) => {
  switch (status) {
    case "pending":
      return <FiClock />;
    case "processing":
      return <FiActivity />;
    case "delivered":
      return <FiCheck />;
    case "canceled":
      return <FiXCircle />;
    default:
      return <FiClock />;
  }
};

const AdminOrdersPage = () => {
  const dispatch = useDispatch();
  const orders = useSelector(selectOrders);

  const orderById = useSelector(selectOrderById);

  const [activeModal, setActiveModal] = useState(null);
  const [statusChangeOrderId, setStatusChangeOrderId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    orderId: null,
  });

  const [statusFilter, setStatusFilter] = useState("all");

  // Load orders on component mount with better auth handling
  useEffect(() => {
    const loadOrders = async () => {
      try {
        // Small delay to ensure auth token is loaded
        if (isInitialLoad) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          setIsInitialLoad(false);
        }

        await dispatch(getOrders({ page: currentPage, limit: 10 })).unwrap();
      } catch (error) {
        console.error("Failed to load orders:", error);

        // If auth error and it's initial load, retry once more with longer delay
        if (
          isInitialLoad &&
          (error.message?.includes("authenticated") ||
            error.message?.includes("401") ||
            error.message?.includes("Authorization"))
        ) {
          console.log("Auth error on initial load - retrying with delay...");
          setTimeout(async () => {
            try {
              await dispatch(
                getOrders({ page: currentPage, limit: 10 })
              ).unwrap();
              setIsInitialLoad(false);
            } catch (retryError) {
              console.error("Retry failed:", retryError);
              setIsInitialLoad(false);
            }
          }, 1000);
        } else {
          setIsInitialLoad(false);
        }
      }
    };

    loadOrders();
  }, [dispatch, currentPage]); // Removed isInitialLoad from dependencies to avoid infinite loop

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearOrdersError());
      dispatch(clearOrderById());
    };
  }, [dispatch]);

  // Funkcija za zatvaranje modala
  const closeModal = () => {
    setActiveModal(null);
    setStatusChangeOrderId(null);
    dispatch(clearOrderById());
  };

  const openModal = (modalId) => {
    setActiveModal(modalId);
  };

  const viewOrderDetails = async (orderId) => {
    try {
      await dispatch(getOrderById(orderId)).unwrap();
      openModal("viewOrder");
    } catch (error) {
      console.error("Failed to fetch order details:", error);
    }
  };

  const openStatusChangeModal = (orderId) => {
    setStatusChangeOrderId(orderId);
    openModal("changeStatus");
  };

  // Funkcija za menjanje statusa
  const updateStatus = async (orderId, newStatus) => {
    try {
      await dispatch(
        updateOrderStatus({ id: orderId, status: newStatus })
      ).unwrap();

      // Refresh orders list
      dispatch(getOrders({ page: currentPage, limit: 10 }));

      closeModal();
    } catch (error) {
      console.error("Failed to update order status:", error);
    }
  };

  const handleDeleteOrder = async () => {
    try {
      await dispatch(deleteOrder(deleteConfirm.orderId))
        .unwrap()
        .then(() => {
          // Refresh orders list
          toast.success("Uspešno ste obrisali porudžbinu.");

          dispatch(getOrders({ page: currentPage, limit: 10 }));

          closeModal();
          setDeleteConfirm({ open: false, orderId: null });
        })
        .catch((err) => toast.error(err.message));
    } catch (error) {
      toast.error("Failed to delete order:", error);
    }
  };

  const getStatusText = (status) => {
    const statusOption = statusOptions.find(
      (option) => option.value === status
    );
    return statusOption ? statusOption.label : status;
  };

  // Funkcije za paginaciju
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < orders.pagination.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Funkcija za otvaranje confirm dijaloga za brisanje
  const openDeleteConfirm = (orderId) => {
    setDeleteConfirm({
      open: true,
      orderId: orderId,
    });
  };

  // Filter orders by status
  const filteredOrders = orders.data.filter((order) => {
    if (statusFilter === "all") return true;
    return order.status === statusFilter;
  });

  if ((orders.loading && !orders.data.length) || isInitialLoad) {
    return (
      <AdminContainer>
        <PageSection>
          <PageHeader>
            <PageTitle>Upravljanje Porudžbinama</PageTitle>
          </PageHeader>

          <Loader title={"porudžbina"} />
        </PageSection>
      </AdminContainer>
    );
  }

  if (orders.error && !isInitialLoad) {
    return (
      <AdminContainer>
        <PageSection>
          <PageHeader>
            <PageTitle>Upravljanje Porudžbinama</PageTitle>
          </PageHeader>
          <div
            style={{ textAlign: "center", padding: "2rem", color: "#e74c3c" }}
          >
            <p>Greška pri učitavanju porudžbina: {orders.error}</p>
            <Button
              $variant="primary"
              onClick={() => {
                dispatch(clearOrdersError());
                dispatch(getOrders({ page: currentPage, limit: 10 }));
              }}
            >
              Pokušaj ponovo
            </Button>
          </div>
        </PageSection>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <PageSection>
        <PageHeader>
          <PageTitle>Upravljanje Porudžbinama</PageTitle>
          <Relative>
            <FilterSelect
              id="status-filter"
              value={statusFilter}
              style={{ paddingRight: "2rem" }}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Svi statusi</option>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </FilterSelect>
            <FiChevronDown
              color="#ff6b35"
              size={20}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            />
          </Relative>
        </PageHeader>

        <TableContainer>
          <Table>
            <thead>
              <tr>
                <TableHeader>Broj Porudžbine</TableHeader>
                <TableHeader>Kupac</TableHeader>
                <TableHeader>Cena</TableHeader>
                <TableHeader>Datum</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Akcije</TableHeader>
              </tr>
            </thead>
            <tbody>
              {orders.loading ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    <Loader title={"porudžbina"} />
                  </TableCell>
                </TableRow>
              ) : filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: "#cccccc",
                    }}
                  >
                    <div style={{ fontSize: "1.2rem", marginBottom: "0.5rem" }}>
                      <FiFileText size={32} color="#666" />
                    </div>
                    <div>
                      {statusFilter === "all"
                        ? "Trenutno nema porudžbina"
                        : `Nema porudžbina sa statusom "${getStatusText(
                            statusFilter
                          )}"`}
                    </div>
                    <div
                      style={{
                        fontSize: "0.9rem",
                        marginTop: "0.5rem",
                        opacity: 0.7,
                      }}
                    >
                      {statusFilter === "all"
                        ? "Nove porudžbine će se prikazati ovde"
                        : "Pokušajte sa drugim statusom"}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>{order.orderNumber}</TableCell>
                    <TableCell>
                      {order.firstName} {order.lastName}
                    </TableCell>
                    <TableCell>{formatPrice(order.itemsPrice)}</TableCell>
                    <TableCell>{formatDate(order.createdAt)}</TableCell>
                    <TableCell>
                      <StatusBadge
                        $status={order.status}
                        onClick={() => openStatusChangeModal(order.id)}
                      >
                        {getOrderStatusIcon(order.status)}
                        {getStatusText(order.status)}
                        <FiEdit3 />
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <ActionButtonGroup>
                        <Button
                          $variant="secondary"
                          onClick={() => viewOrderDetails(order.id)}
                          disabled={orders.loading}
                        >
                          <FiEye />
                          Pregled
                        </Button>
                        <Button
                          $variant="danger"
                          onClick={() => openDeleteConfirm(order.id)}
                          disabled={orders.deleteLoading}
                          title="Obriši porudžbinu"
                        >
                          <FiTrash2 size={16} />
                        </Button>
                      </ActionButtonGroup>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>

          <PaginationContainer>
            <PaginationInfo>
              Prikazuje se {filteredOrders.length} od{" "}
              {orders.pagination.totalOrders} porudžbina
              {statusFilter !== "all" &&
                ` (filtrirano po statusu: ${getStatusText(statusFilter)})`}
            </PaginationInfo>

            <div>
              <PaginationButton
                onClick={goToPreviousPage}
                disabled={currentPage === 1 || orders.loading}
              >
                <FiChevronLeft />
              </PaginationButton>

              {Array.from(
                { length: orders.pagination.totalPages },
                (_, i) => i + 1
              ).map((page) => (
                <PaginationButton
                  key={page}
                  onClick={() => goToPage(page)}
                  $active={currentPage === page}
                  disabled={orders.loading}
                >
                  {page}
                </PaginationButton>
              ))}

              <PaginationButton
                onClick={goToNextPage}
                disabled={
                  currentPage === orders.pagination.totalPages || orders.loading
                }
              >
                <FiChevronRight />
              </PaginationButton>
            </div>
          </PaginationContainer>
        </TableContainer>
      </PageSection>

      {/* Modal za pregled ordera */}
      <Modal $active={activeModal === "viewOrder"}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              Detalji Porudžbine - {orderById.data?.orderNumber}
            </ModalTitle>
            <ModalClose onClick={closeModal}>
              <FiX />
            </ModalClose>
          </ModalHeader>

          {orderById.loading ? (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              <FiLoader
                size={24}
                style={{
                  animation: "spin 1s linear infinite",
                  color: "#ff6b35",
                }}
              />
              <p style={{ color: "#cccccc" }}>Učitavanje detalja...</p>
            </div>
          ) : orderById.data ? (
            <div>
              <CustomerInfoContainer>
                <CustomerInfoTitle>Informacije o kupcu</CustomerInfoTitle>
                <CustomerInfoGrid>
                  <CustomerInfoItem>
                    <CustomerInfoLabel>Ime i prezime:</CustomerInfoLabel>
                    <CustomerInfoValue>
                      {orderById.data.firstName} {orderById.data.lastName}
                    </CustomerInfoValue>
                  </CustomerInfoItem>
                  <CustomerInfoItem>
                    <CustomerInfoLabel>Email:</CustomerInfoLabel>
                    <CustomerInfoValue>
                      {orderById.data.email}
                    </CustomerInfoValue>
                  </CustomerInfoItem>
                  <CustomerInfoItem>
                    <CustomerInfoLabel>Telefon:</CustomerInfoLabel>
                    <CustomerInfoValue>
                      {orderById.data.phone}
                    </CustomerInfoValue>
                  </CustomerInfoItem>
                  <CustomerInfoItem>
                    <CustomerInfoLabel>Adresa:</CustomerInfoLabel>
                    <CustomerInfoValue>
                      {orderById.data.address}
                    </CustomerInfoValue>
                  </CustomerInfoItem>
                  <CustomerInfoItem>
                    <CustomerInfoLabel>Grad:</CustomerInfoLabel>
                    <CustomerInfoValue>{orderById.data.city}</CustomerInfoValue>
                  </CustomerInfoItem>
                  <CustomerInfoItem>
                    <CustomerInfoLabel>Poštanski broj:</CustomerInfoLabel>
                    <CustomerInfoValue>
                      {orderById.data.zipCode}
                    </CustomerInfoValue>
                  </CustomerInfoItem>
                  {orderById.data.details && (
                    <CustomerInfoItem style={{ gridColumn: "1 / -1" }}>
                      <CustomerInfoLabel>Napomena:</CustomerInfoLabel>
                      <CustomerInfoValue>
                        {orderById.data.details}
                      </CustomerInfoValue>
                    </CustomerInfoItem>
                  )}
                </CustomerInfoGrid>
              </CustomerInfoContainer>

              <OrderItemsContainer>
                <OrderItemsTitle>Stavke porudžbine</OrderItemsTitle>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>Proizvod</TableHeader>
                      <TableHeader>Bend</TableHeader>
                      <TableHeader>Kategorija</TableHeader>
                      <TableHeader>Veličina</TableHeader>
                      <TableHeader>Količina</TableHeader>
                      <TableHeader>Cena</TableHeader>
                      <TableHeader>Ukupno</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {orderById.data.orderItems?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>
                          {item.product?.band?.name || "N/A"}
                        </TableCell>
                        <TableCell>
                          {item.product?.category?.name || "N/A"}
                        </TableCell>
                        <TableCell>{item.size || "N/A"}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{formatPrice(item.price)}</TableCell>
                        <TableCell>{formatPrice(item.totalPrice)}</TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </OrderItemsContainer>

              <OrderSummaryContainer>
                <OrderSummaryGrid>
                  <OrderSummaryItem>
                    <CustomerInfoLabel>Datum porudžbine:</CustomerInfoLabel>
                    <CustomerInfoValue>
                      {new Date(orderById.data.createdAt).toLocaleDateString(
                        "sr-Latn-RS",
                        {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )}
                    </CustomerInfoValue>
                  </OrderSummaryItem>
                  <OrderSummaryItem>
                    <CustomerInfoLabel>Status:</CustomerInfoLabel>
                    <StatusBadge
                      $status={orderById.data.status}
                      onClick={() => openStatusChangeModal(orderById.data.id)}
                    >
                      {getOrderStatusIcon(orderById.data.status)}

                      {getStatusText(orderById.data.status)}
                      <FiEdit3 />
                    </StatusBadge>
                  </OrderSummaryItem>
                  <OrderSummaryItem>
                    <CustomerInfoLabel>Proizvodi:</CustomerInfoLabel>
                    <CustomerInfoValue>
                      {formatPrice(orderById.data.itemsPrice)}
                    </CustomerInfoValue>
                  </OrderSummaryItem>
                  <OrderSummaryItem>
                    <CustomerInfoLabel>Dostava:</CustomerInfoLabel>
                    <CustomerInfoValue>
                      {orderById.data.deliveryPrice === 0
                        ? "BESPLATNO"
                        : formatPrice(orderById.data.deliveryPrice)}
                    </CustomerInfoValue>
                  </OrderSummaryItem>
                  {orderById.data.coupon && (
                    <OrderSummaryItem>
                      <CustomerInfoLabel>
                        Kupon ({orderById.data.coupon.code}):
                      </CustomerInfoLabel>
                      <CustomerInfoValue style={{ color: "#27ae60" }}>
                        -{orderById.data.coupon.discount}%
                      </CustomerInfoValue>
                    </OrderSummaryItem>
                  )}
                  <OrderSummaryItem $total>
                    <CustomerInfoLabel>UKUPNO:</CustomerInfoLabel>
                    <CustomerInfoValue $total>
                      {formatPrice(orderById.data.totalPrice)}
                    </CustomerInfoValue>
                  </OrderSummaryItem>
                </OrderSummaryGrid>
              </OrderSummaryContainer>
            </div>
          ) : (
            <div
              style={{ textAlign: "center", padding: "2rem", color: "#e74c3c" }}
            >
              <p>Greška pri učitavanju detalja porudžbine.</p>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Modal za promenu statusa */}
      <StatusChangeModal $active={activeModal === "changeStatus"}>
        <StatusChangeContent>
          <ModalHeader>
            <StatusChangeTitle>Promeni Status Porudžbine</StatusChangeTitle>
            <ModalClose onClick={closeModal}>
              <FiX />
            </ModalClose>
          </ModalHeader>

          <StatusOptionsContainer>
            {statusOptions.map((option) => (
              <StatusOption
                key={option.value}
                $status={option.value}
                onClick={() => updateStatus(statusChangeOrderId, option.value)}
                disabled={orders.updateLoading}
              >
                {getOrderStatusIcon(option.value)}
                {orders.updateLoading ? <FiLoader size={16} /> : option.label}
              </StatusOption>
            ))}
          </StatusOptionsContainer>
        </StatusChangeContent>
      </StatusChangeModal>

      <AnimatePresence>
        {deleteConfirm.open && (
          <ConfirmModal
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDeleteConfirm({ open: false, orderId: null })}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Potvrdi brisanje</h3>
              <p>Da li ste sigurni da želite da obrišete porudžbinu?</p>
              <div className="modal-actions">
                <Button
                  $variant="secondary"
                  onClick={() =>
                    setDeleteConfirm({
                      open: false,
                      orderId: null,
                    })
                  }
                  disabled={orders.deleteLoading}
                >
                  Otkaži
                </Button>
                <Button
                  $variant="danger"
                  onClick={handleDeleteOrder}
                  disabled={orders.deleteLoading}
                >
                  {orders.deleteLoading ? "Brišem..." : "Obriši"}
                </Button>
              </div>
            </div>
          </ConfirmModal>
        )}
      </AnimatePresence>
    </AdminContainer>
  );
};

export default AdminOrdersPage;
